import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'lpd-pwa-update-dismissed-version';

/**
 * PWAUpdateBanner — toont een banner zodra een nieuwe service worker is
 * geïnstalleerd en in waiting state staat. Klik op "Verversen" doet
 * skipWaiting + reload zodat de nieuwste build direct actief wordt.
 *
 * Mounted alleen op V2 laadplein-designer routes — bewust niet globaal,
 * om V1-pad (App.tsx) niet te raken.
 */
export const PWAUpdateBanner = () => {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.warn('[PWAUpdateBanner] SW registration error:', error);
    },
  });

  const [dismissedToken, setDismissedToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(DISMISS_KEY);
    } catch {
      return null;
    }
  });

  // Build-token: changes per build via Vite's import.meta.env. Falls back
  // to a session-stable value when not present.
  const buildToken =
    (import.meta as { env?: Record<string, string | undefined> }).env
      ?.VITE_BUILD_ID ??
    (import.meta as { env?: Record<string, string | undefined> }).env?.MODE ??
    'unknown';

  useEffect(() => {
    // If a new build is available we drop the previous dismiss token so
    // the banner can re-appear for the new version.
    if (needRefresh && dismissedToken && dismissedToken !== buildToken) {
      try {
        window.localStorage.removeItem(DISMISS_KEY);
        setDismissedToken(null);
      } catch {
        /* ignore */
      }
    }
  }, [needRefresh, dismissedToken, buildToken]);

  const isDismissedForCurrentBuild = dismissedToken === buildToken;

  if (!needRefresh || isDismissedForCurrentBuild) {
    return null;
  }

  const handleRefresh = () => {
    void updateServiceWorker(true);
  };

  const handleLater = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, buildToken);
    } catch {
      /* ignore */
    }
    setDismissedToken(buildToken);
    setNeedRefresh(false);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[9999] max-w-sm rounded-lg border border-border bg-background shadow-lg"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <RefreshCw className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">
            Nieuwe versie beschikbaar
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Herlaad de pagina om de laatste updates te gebruiken.
          </p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={handleRefresh}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Verversen
            </Button>
            <Button size="sm" variant="ghost" onClick={handleLater}>
              Later
            </Button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Sluiten"
          onClick={handleLater}
          className="-mr-1 -mt-1 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PWAUpdateBanner;
